import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Loader2,
  Merge,
  CheckCircle2,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency, formatRelative } from '@/lib/utils'
import type {
  APIResponse,
  CustomerDuplicateCandidate,
} from '@/types'

interface AdminCustomerMergeDialogProps {
  sourceID: string | null
  sourceName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onMerged?: (targetID: string) => void
}

/**
 * Merge UI. The source customer (the one the user is currently viewing)
 * is folded into a chosen target — orders re-attach, missing fields
 * COALESCE onto the target, source is soft-deleted via merged_into. An
 * audit log row is written server-side; this is reversible only by
 * restoring from a backup so the confirm step is explicit.
 */
export function AdminCustomerMergeDialog({
  sourceID,
  sourceName,
  open,
  onOpenChange,
  onMerged,
}: AdminCustomerMergeDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [selectedTarget, setSelectedTarget] = useState<string | null>(null)

  const dupesQuery = useQuery<APIResponse<CustomerDuplicateCandidate[]>>({
    queryKey: ['customer-duplicates', sourceID],
    queryFn: () => apiClient.suggestCustomerDuplicates(sourceID as string),
    enabled: Boolean(sourceID) && open,
    staleTime: 30_000,
  })

  const mergeMutation = useMutation({
    mutationFn: ({ source, target }: { source: string; target: string }) =>
      apiClient.mergeCustomers(source, target),
    onSuccess: () => {
      toast({ title: 'Customers merged', description: 'Orders re-attached to the surviving record.' })
      queryClient.invalidateQueries({ queryKey: ['customers', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['customer-segments'] })
      if (sourceID) queryClient.removeQueries({ queryKey: ['customer', sourceID] })
      if (selectedTarget) {
        queryClient.invalidateQueries({ queryKey: ['customer', selectedTarget] })
        onMerged?.(selectedTarget)
      }
      onOpenChange(false)
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Merge failed', description: msg, variant: 'destructive' })
    },
  })

  const candidates = dupesQuery.data?.data ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Merge className="h-5 w-5" />
            Find &amp; merge duplicate
          </DialogTitle>
          <DialogDescription>
            Pick the record to keep. Orders attached to{' '}
            <span className="font-medium text-foreground">
              {sourceName || 'this customer'}
            </span>{' '}
            will be re-attached to the chosen target and this row will be soft-deleted (audit-logged).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {dupesQuery.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : candidates.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-center text-sm text-muted-foreground">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              <span>No likely duplicates found — this looks like a unique customer.</span>
            </div>
          ) : (
            candidates.map((c) => {
              const isSelected = selectedTarget === c.id
              const phoneConflict = (c.conflicts ?? []).includes('phone')
              const emailConflict = (c.conflicts ?? []).includes('email')
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedTarget(c.id)}
                  className={`w-full rounded-lg border text-left p-3 transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                      : 'border-border/80 hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground truncate">
                          {c.display_name || (
                            <span className="italic text-muted-foreground">unnamed</span>
                          )}
                        </span>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                          {c.match_reason}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {c.phone_normalized || c.phone ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono">
                              {c.phone_normalized || c.phone}
                            </span>
                            {phoneConflict && (
                              <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-medium uppercase tracking-wider text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                                Different phone
                              </span>
                            )}
                          </span>
                        ) : null}
                        {c.email ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span>{c.email}</span>
                            {emailConflict && (
                              <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-medium uppercase tracking-wider text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                                Different email
                              </span>
                            )}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <div className="font-semibold tabular-nums">
                        {formatCurrency(c.lifetime_spend)}
                      </div>
                      <div className="text-muted-foreground tabular-nums">
                        {c.visit_count} {c.visit_count === 1 ? 'visit' : 'visits'}
                      </div>
                      {c.last_visit_at ? (
                        <div className="text-muted-foreground mt-0.5">
                          {formatRelative(c.last_visit_at)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </button>
              )
            })
          )}
        </div>

        {selectedTarget && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                This will repoint all orders from this customer to the selected
                record and soft-delete the current one. Reversible only via
                database restore.
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mergeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selectedTarget || mergeMutation.isPending || !sourceID}
            onClick={() => {
              if (sourceID && selectedTarget) {
                mergeMutation.mutate({ source: sourceID, target: selectedTarget })
              }
            }}
            className="gap-1.5"
          >
            {mergeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Merge className="h-4 w-4" />
            )}
            Merge into selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
